import { ChannelMigrationCard } from "@/components/ChannelMigrationCard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { ShopHealthCard } from "@/components/ShopHealthCard";
import { DeliveryNotificationsCard } from "@/components/DeliveryNotificationsCard";
import { ArrowLeft, ArrowRight, CheckCircle2, CircleAlert, Download, Filter, Layers, Loader2, Pencil, RefreshCw, Store, UploadCloud, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { ChannelBackorderCard } from "@/components/BackorderSettings";
import { ChannelLogo } from "@/components/ChannelLogo";
import { MissingSkuCard, RefusedRequestsCard } from "@/components/ShopHints";
import { ShippingMappingCard } from "@/components/ShippingMappingCard";
import { SftpChannelDetail } from "@/components/Sftp";
import { KINDS as MARKETPLACES, MarketplaceChannelDetail } from "@/components/Marketplaces";
import { FetchOrdersDialog } from "@/components/FetchOrdersDialog";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, isMarketplace, type ChannelDetail, type ImportFilter, type ShopifyImportState, type ShopStockPush } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { ChannelStockSyncCard } from "@/components/StockSync";
import { t, msg } from "@/lib/i18n";

import { serverText } from "@/lib/server-text";
const STATUSES: { key: ImportFilter["statuses"][number]; label: string }[] = [
  { key: "ACTIVE", label: msg("Aktiv") },
  { key: "DRAFT", label: msg("Entwurf") },
  { key: "ARCHIVED", label: msg("Archiviert") },
];

const running = (s: ShopifyImportState | null | undefined) => s?.status === "exporting" || s?.status === "importing";

/** Auswahl mehrerer Werte: Vorschläge aus dem Shop oder freie Eingabe (Enter / Komma) */
function TokenSelect({ value, onChange, options = [], placeholder, allowCustom = true, mono }: { value: string[]; onChange: (v: string[]) => void; options?: string[]; placeholder: string; allowCustom?: boolean; mono?: boolean }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const lower = value.map((v) => v.toLowerCase());
  const suggestions = options.filter((o) => !lower.includes(o.toLowerCase()) && o.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);
  const add = (v: string) => {
    const tv = v.trim();
    if (!tv || lower.includes(tv.toLowerCase())) return;
    onChange([...value, tv]);
    setQ("");
    setActive(0);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(suggestions.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (open && suggestions[active]) add(suggestions[active]!);
      else if (allowCustom) add(q);
    } else if (e.key === "Backspace" && !q && value.length) onChange(value.slice(0, -1));
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div className="relative">
      <div onClick={() => ref.current?.focus()} className="min-h-8 flex flex-wrap items-center gap-1 rounded-lg border border-border bg-surface px-1.5 py-1 focus-within:border-fg-muted focus-within:ring-2 focus-within:ring-fg/10 cursor-text">
        {value.map((v) => (
          <span key={v} className={cx("inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-surface-2 border border-border text-[12px]", mono && "font-mono")}>
            {v}
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(value.filter((x) => x !== v)); }} className="size-4 grid place-items-center rounded text-fg-subtle hover:text-fg" aria-label={t("{v} entfernen", { v })}><X className="size-3" /></button>
          </span>
        ))}
        <input
          ref={ref}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => { setOpen(false); if (allowCustom && q.trim()) add(q); }, 150)}
          onKeyDown={onKey}
          placeholder={value.length ? "" : placeholder}
          className={cx("flex-1 min-w-[120px] h-6 bg-transparent px-1 text-[13px] outline-none placeholder:text-fg-subtle", mono && "font-mono")}
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-xl border border-border bg-surface shadow-xl py-1 animate-fade-in">
          {suggestions.map((s, i) => (
            <li key={s}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => add(s)} className={cx("w-full text-left px-3 py-1.5 text-[13px]", i === active && "bg-surface-2/70")}>{s}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid sm:grid-cols-[200px_1fr] gap-x-6 gap-y-1.5 py-3.5 border-b border-border last:border-0">
      <div>
        <div className="text-[13px] font-medium">{t(label)}</div>
        {hint && <div className="text-[12px] text-fg-subtle">{t(hint)}</div>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: ReactNode }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className="flex items-center gap-2.5 text-left">
      <span className={cx("w-8 h-[18px] shrink-0 rounded-full p-0.5 transition-colors", on ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", on && "translate-x-3.5")} /></span>
      <span className="text-[13px]">{label}</span>
    </button>
  );
}

type SyncTab = "articles" | "stock" | "orders";
const SYNC_TABS: { key: SyncTab; label: string }[] = [
  { key: "articles", label: msg("Artikel") },
  { key: "stock", label: msg("Bestand") },
  { key: "orders", label: msg("Aufträge") },
];

/** Kurzbeschreibung der Artikelauswahl für die Übersicht */
function filterSummary(f: ImportFilter): string {
  if (f.mode === "all") return "Alle Artikel";
  const parts: string[] = [];
  if (f.statuses.length && f.statuses.length < 3) parts.push(f.statuses.map((s) => STATUSES.find((x) => x.key === s)?.label ?? s).join(", "));
  if (f.vendors.length) parts.push(`Hersteller: ${f.vendors.join(", ")}`);
  if (f.productTypes.length) parts.push(`Typ: ${f.productTypes.join(", ")}`);
  if (f.tagsAny.length) parts.push(`Tag: ${f.tagsAny.join(", ")}`);
  if (f.tagsExclude.length) parts.push(`ohne Tag: ${f.tagsExclude.join(", ")}`);
  if (f.skuPrefixes.length) parts.push(`SKU: ${f.skuPrefixes.map((p) => `${p}…`).join(", ")}`);
  if (f.onlyShippable) parts.push("nur versandpflichtig");
  if (f.onlyTracked) parts.push("nur mit Bestandsführung");
  return parts.length ? parts.join(" · ") : "Alle Artikel";
}

export function ChannelDetailPage() {
  const { channelId = "" } = useParams();
  const { tenantId, role, me: meData } = useTenant();
  const [params, setParams] = useSearchParams();
  const q = useQuery({
    queryKey: ["channel", tenantId, channelId],
    queryFn: () => api.channels.get(tenantId, channelId),
    refetchInterval: (query) => (running(query.state.data?.import) || query.state.data?.stockPush?.status === "running" ? 2000 : false),
  });
  const d = q.data;
  const syncParam = params.get("sync");
  const syncTab: SyncTab | null = syncParam ? (syncParam === "stock" || syncParam === "orders" ? syncParam : "articles") : null;
  const openSync = (tab: SyncTab | null) => {
    const next = new URLSearchParams(params);
    if (tab) next.set("sync", tab === "articles" ? "1" : tab);
    else next.delete("sync");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (params.get("connected") === "1") {
      toast.success("Shop verbunden", { description: msg("Webhooks und Fulfillment-Service werden eingerichtet.") });
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  if (q.isLoading) return <div className="px-4 md:px-8 py-8 space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-80 w-full" /></div>;
  if (!d) return <div className="px-4 md:px-8 py-8 text-fg-muted">{t("Shop nicht gefunden.")} <Link className="underline" to="/channels">{t("Zur Übersicht")}</Link></div>;
  if (isMarketplace(d.kind))
    return (
      <div className="pb-16">
        <PageHeader icon={<ChannelLogo kind={d.kind} size="lg" />} title={d.name} description={d.kind === "odoo" ? t("Odoo – Aufträge, Versandmeldung mit Charge und Sendungsnummer, Artikel, Bestand je Charge, eigene Felder; alle 3 Minuten") : t("{label} – Aufträge, Versandmeldung mit Tracking und Bestand, alle 3 Minuten", { label: MARKETPLACES[d.kind].label })} actions={<Link to="/channels"><Button variant="ghost" icon={<ArrowLeft className="size-4" />}>{t("Alle Shops")}</Button></Link>} />
        <div className="px-4 md:px-8">
          <MarketplaceChannelDetail channelId={d.id} shippingMapping={d.shippingMapping ?? { mappings: [], fallbackMethodId: null }} seen={d.shopMethodsSeen ?? []} backorder={d.backorder ?? {}} />
        </div>
      </div>
    );
  if (d.kind === "sftp")
    return (
      <div className="pb-16">
        <PageHeader icon={<ChannelLogo kind="sftp" size="lg" />} title={d.name} description={t("SFTP-Anbindung – Aufträge als CSV abholen, Versandbestätigungen zurückschreiben")} actions={<Link to="/channels"><Button variant="ghost" icon={<ArrowLeft className="size-4" />}>{t("Alle Shops")}</Button></Link>} />
        <div className="px-4 md:px-8">
          <SftpChannelDetail channelId={d.id} shippingMapping={d.shippingMapping ?? { mappings: [], fallbackMethodId: null }} seen={d.shopMethodsSeen ?? []} backorder={d.backorder ?? {}} />
        </div>
      </div>
    );

  const editable = canManage(role);
  const autoLabel = { all: "Alle automatisch anfordern", paid: "Nur bezahlte automatisch", off: "Manuell in Shopify" }[d.autoRequestFulfillment ?? "all"];
  const tiles: { tab: SyncTab; title: string; value: string; hint: string }[] = [
    { tab: "articles", title: msg("Artikel"), value: filterSummary(d.filter), hint: d.filter.skuFallback === "variantId" ? "Ohne SKU: Varianten-ID als SKU" : "Ohne SKU: übersprungen" },
    { tab: "stock", title: msg("Bestand"), value: d.pushStock === false ? t("Bestand wird nicht gemeldet – ihr pflegt ihn in Shopify") : (d.locationScope ?? "all") === "all" ? "Alle importierten Artikel am Logiful-Standort" : "Nur für Logiful freigegebene Artikel", hint: d.pushStock === false ? ((d.locationScope ?? "all") === "all" ? t("Alle importierten Artikel am Logiful-Standort") : t("Nur für Logiful freigegebene Artikel")) : d.fillMissingSku ? "Fehlende SKUs werden in Shopify ergänzt" : "Fehlende SKUs werden nicht ergänzt" },
    { tab: "orders", title: msg("Aufträge"), value: autoLabel, hint: msg("Abruf alle 3 Minuten") },
  ];

  return (
    <div className="pb-16">
      <PageHeader
        icon={<ChannelLogo kind={d.kind ?? "shopify"} size="lg" />}
        title={d.name}
        description={d.externalAccount}
        actions={
          <>
            <Link to="/channels"><Button variant="ghost" icon={<ArrowLeft className="size-4" />}>{t("Alle Shops")}</Button></Link>
            {editable && <Button variant="primary" icon={<RefreshCw className="size-4" />} onClick={() => openSync("articles")}>{t("Synchronisation einstellen")}</Button>}
          </>
        }
      />
      <div className="px-4 md:px-8 space-y-4 max-w-5xl">
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          {d.status === "active" ? <Badge tone="success">{t("verbunden")}</Badge> : d.status === "pending" ? <Badge tone="warning">{t("wird eingerichtet")}</Badge> : <Badge tone="danger">{t("getrennt")}</Badge>}
          {d.ownApp && <Badge>{t("eigene Shopify-App")}</Badge>}
          {d.import?.catalogSize != null && <span className="text-fg-muted inline-flex items-center gap-1"><Layers className="size-3.5" /> {fmtNumber(d.import.catalogSize)} {t("Varianten im Shop")}</span>}
        </div>
        {d.lastError && <p className="text-[12.5px] text-danger bg-danger-soft rounded-lg px-3 py-2">{d.lastError}</p>}

        {d.import?.status === "awaiting_filter" && (
          <div className="rounded-xl border border-warning/30 bg-warning-soft/60 p-4 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-medium flex items-center gap-2"><Filter className="size-4 text-warning" /> {t("Großes Sortiment – bitte festlegen, welche Artikel ins Lager sollen")}</p>
              <p className="mt-1 text-[13px] text-fg-muted">{t("Der Shop hat")} {fmtNumber(d.import.catalogSize ?? 0)} {t("Varianten. Damit nur lagerrelevante Artikel übertragen werden, startet der Import erst nach eurer Auswahl.")}</p>
            </div>
            {editable && <Button variant="primary" onClick={() => openSync("articles")}>{t("Artikelauswahl festlegen")}</Button>}
          </div>
        )}

        <ShopHealthCard channelId={d.id} editable={editable} />
        {d.kind === "shopify" && meData.user.staffRole === "staff_admin" && <ChannelMigrationCard tenantId={tenantId} channelId={d.id} />}
        <ImportStatus state={d.import} shop={d.externalAccount} />
        <RefusedRequestsCard items={d.refusedRequests ?? []} />
        {(d.missingSku?.count ?? 0) > 0 && <MissingSkuCard channelId={d.id} missing={d.missingSku!} fill={!!d.fillMissingSku} editable={editable} />}

        {/* Synchronisation: kompakte Übersicht, Einstellungen im Dialog */}
        <Card className="overflow-hidden">
          <div className="px-5 pt-4 pb-3 flex flex-wrap items-center gap-3 border-b border-border">
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold text-[14px] flex items-center gap-2"><RefreshCw className="size-4 text-fg-muted" /> {t("Synchronisation")}</h2>
              <p className="text-[12.5px] text-fg-muted">{t("Was zwischen Shop, Portal und Lager abgeglichen wird")}</p>
            </div>
            {editable && (
              <div className="flex flex-wrap gap-2">
                {d.pushStock !== false && <PushStockButton channelId={d.id} running={d.stockPush?.status === "running"} />}
                <Button size="sm" icon={<Pencil className="size-3.5" />} onClick={() => openSync("articles")}>{t("Bearbeiten")}</Button>
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {tiles.map((tv) => (
              <button key={tv.tab} type="button" disabled={!editable} onClick={() => openSync(tv.tab)} className="flex flex-col justify-start text-left px-5 py-4 hover:bg-surface-2/60 disabled:hover:bg-transparent disabled:cursor-default group">
                <span className="flex items-center gap-1.5 text-[12px] font-medium text-fg-subtle">{t(tv.title)}{editable && <Pencil className="size-3 opacity-0 group-hover:opacity-100 transition-opacity" />}</span>
                <span className="mt-1 block text-[13px] font-medium line-clamp-2">{tv.value}</span>
                <span className="mt-0.5 block text-[12px] text-fg-muted">{t(tv.hint)}</span>
              </button>
            ))}
          </div>
          {d.stockPush && <StockPushStatus s={d.stockPush} />}
        </Card>

        <h2 className="pt-3 text-[12px] font-semibold uppercase tracking-wide text-fg-subtle">{t("Auftragsverarbeitung")}</h2>
        <ShippingMappingCard sourceLabel="Shopify" key={JSON.stringify(d.shippingMapping)} channelId={d.id} mapping={d.shippingMapping ?? { mappings: [], fallbackMethodId: null }} seen={d.shopMethodsSeen ?? []} editable={editable} />
        <DeliveryNotificationsCard channelId={d.id} shop={d.externalAccount} value={d.deliveryNotifications ?? "none"} klaviyoConnected={!!d.klaviyoConnected} />
        {d.pushStock !== false && <ChannelStockSyncCard key={JSON.stringify(d.stockSync)} channelId={d.id} value={d.stockSync} />}
        <ChannelBackorderCard channelId={d.id} override={d.backorder ?? {}} />
      </div>

      {editable && <SyncDialog d={d} tab={syncTab} onTab={openSync} />}
    </div>
  );
}

/** Synchronisationseinstellungen im Dialog: Artikel (Auswahl & Import), Bestand (Standort, SKUs), Aufträge (Abruf) */
function SyncDialog({ d, tab, onTab }: { d: ChannelDetail; tab: SyncTab | null; onTab: (tv: SyncTab | null) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  return (
    <Dialog.Root open={tab !== null} onOpenChange={(o) => !o && onTab(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(820px,calc(100vw-24px))] max-h-[calc(100vh-24px)] flex flex-col rounded-2xl border border-border bg-surface shadow-2xl animate-pop-in">
          <div className="px-6 pt-5 flex items-start gap-3">
            <ChannelLogo kind={d.kind ?? "shopify"} />
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{t("Synchronisation einstellen")}</Dialog.Title>
              <Dialog.Description className="text-[12.5px] text-fg-muted truncate">{d.name} · {d.externalAccount}</Dialog.Description>
            </div>
            <Dialog.Close asChild><button className="size-8 grid place-items-center rounded-lg text-fg-muted hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></button></Dialog.Close>
          </div>
          <div className="px-6 mt-4 border-b border-border flex gap-5" role="tablist">
            {SYNC_TABS.map((tv) => (
              <button key={tv.key} role="tab" aria-selected={tab === tv.key} onClick={() => onTab(tv.key)} className={cx("-mb-px pb-2.5 border-b-2 text-[13px] font-medium transition-colors", tab === tv.key ? "border-ink text-fg" : "border-transparent text-fg-muted hover:text-fg")}>{t(tv.label)}</button>
            ))}
          </div>
          <SyncFlow tab={tab} d={d} />
          <div className="flex-1 min-h-0 overflow-y-auto">
            {tab === "articles" && <NightlySyncSection channelId={d.id} on={!!d.nightlyProductSync} at={d.nightlyProductSyncAt ?? null} />}
            {tab === "articles" && (
              <ImportSettings key={d.filterSaved ? "saved" : "new"} channelId={d.id} initial={d.filter} busy={running(d.import)} onStarted={() => { void qc.invalidateQueries({ queryKey: ["channel", tenantId, d.id] }); onTab(null); }} />
            )}
            {tab === "stock" && (
              <div className="p-6 space-y-7">
                <PushStockSection channelId={d.id} on={d.pushStock !== false} />
                <LocationScopeSection channelId={d.id} scope={d.locationScope ?? "all"} editable />
                <FillSkuSection channelId={d.id} fill={!!d.fillMissingSku} missing={d.missingSku?.count ?? 0} />
              </div>
            )}
            {tab === "orders" && (
              <div className="p-6 space-y-7">
                <AutoRequestSection channelId={d.id} mode={d.autoRequestFulfillment ?? "all"} editable />
                <TrackingLinkSection channelId={d.id} mode={d.trackingLink ?? "warehouse"} />
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Kopf je Reiter: was fließt wohin, wann automatisch – und die Aktion „jetzt“. Darunter stehen die Einstellungen,
 * die festlegen, WIE synchronisiert wird.
 */
function SyncFlow({ tab, d }: { tab: SyncTab | null; d: ChannelDetail }) {
  if (!tab) return null;
  const flow = {
    articles: { from: "Shopify", to: "Portal & Lager", what: "Artikel und Stammdaten", when: "Neue und geänderte Artikel kommen automatisch per Webhook", action: null },
    stock: d.pushStock === false
      ? { from: "Lager", to: "Shopify", what: t("Bestand wird nicht gemeldet"), when: t("Ausgeschaltet – ihr pflegt den Bestand in Shopify"), action: null }
      : { from: "Lager", to: "Shopify", what: "Verfügbarer Bestand am Logiful-Standort", when: "Änderungen werden automatisch innerhalb weniger Sekunden gemeldet", action: <PushStockButton channelId={d.id} running={d.stockPush?.status === "running"} /> },
    orders: { from: "Shopify", to: "Portal & Lager", what: "Aufträge an der Logiful-Location", when: "Automatisch alle 3 Minuten", action: <FetchOrdersButton channelId={d.id} /> },
  }[tab];
  return (
    <div className="mx-6 mt-4 rounded-xl border border-border bg-surface-2/50 px-4 py-3 flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium flex items-center gap-2">{flow.from} <ArrowRight className="size-3.5 text-fg-subtle" /> {flow.to}<span className="font-normal text-fg-muted">· {flow.what}</span></p>
        <p className="text-[12px] text-fg-muted mt-0.5">{flow.when}{flow.action ? t(" – oder jetzt sofort:") : tab === "articles" ? t(". Ein vollständiger Import läuft über den Knopf unten.") : "."}</p>
      </div>
      {flow.action}
    </div>
  );
}

function FetchOrdersButton({ channelId }: { channelId: string }) {
  const { tenantId } = useTenant();
  const fetch = useMutation({
    mutationFn: () => api.channels.fetchOrders(tenantId, channelId),
    onSuccess: () => toast.success("Aufträge werden abgerufen", { description: msg("Neue Aufträge erscheinen in wenigen Sekunden unter Aufträge.") }),
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Abruf fehlgeschlagen") : "Abruf fehlgeschlagen"),
  });
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" icon={<RefreshCw className="size-3.5" />} loading={fetch.isPending} onClick={() => fetch.mutate()}>{t("Jetzt abrufen")}</Button>
      <Button size="sm" variant="ghost" icon={<Download className="size-3.5" />} onClick={() => setOpen(true)}>{t("Gezielt abrufen")}</Button>
      <FetchOrdersDialog open={open} onClose={() => setOpen(false)} channelId={channelId} kind="shopify" label={t("Shopify")} />
    </>
  );
}

/** Alle verknüpften Bestände jetzt an Shopify übertragen – z. B. nach manuellen Änderungen in Shopify */
function PushStockButton({ channelId, running }: { channelId: string; running: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const push = useMutation({
    mutationFn: () => api.channels.pushStock(tenantId, channelId),
    onSuccess: () => {
      toast.success("Bestandsübertragung gestartet", { description: msg("Alle verknüpften Artikel bekommen den aktuellen Bestand aus dem Lager.") });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Übertragung konnte nicht gestartet werden") : "Übertragung konnte nicht gestartet werden"),
  });
  return (
    <Button
      size="sm"
      icon={<UploadCloud className="size-3.5" />}
      loading={push.isPending || running}
      disabled={running}
      onClick={() => confirm("Alle verknüpften Bestände jetzt an Shopify übertragen? Der Bestand am Logiful-Standort wird für jeden Artikel auf den verkaufbaren Lagerbestand gesetzt – auch dort, wo er in Shopify von Hand geändert wurde.") && push.mutate()}
    >
      {running ? t("Bestände werden übertragen …") : t("Alle Bestände übertragen")}
    </Button>
  );
}

function StockPushStatus({ s }: { s: ShopStockPush }) {
  const skipped = [s.withoutStock > 0 && `${fmtNumber(s.withoutStock)} ohne bekannten Lagerbestand`, s.notAtLocation > 0 && `${fmtNumber(s.notAtLocation)} nicht am Logiful-Standort`].filter(Boolean).join(" · ");
  return (
    <div className={cx("px-5 py-2.5 border-t border-border text-[12.5px] flex items-start gap-2", s.status === "failed" ? "bg-danger-soft text-danger" : "text-fg-muted")}>
      {s.status === "running" ? <Loader2 className="size-3.5 mt-0.5 animate-spin shrink-0" /> : s.status === "done" ? <CheckCircle2 className="size-3.5 mt-0.5 text-success shrink-0" /> : <CircleAlert className="size-3.5 mt-0.5 shrink-0" />}
      <span>
        {s.status === "running" && <>{t("Bestände werden an Shopify übertragen …")}{s.products > 0 && t(" {v} Artikel bisher", { v: fmtNumber(s.products) })}</>}
        {s.status === "done" && <>{t("Alle Bestände übertragen")} {fmtRelative(s.finishedAt ?? s.startedAt)}: <b className="text-fg">{fmtNumber(s.products)} {t("Artikel")}</b>, {fmtNumber(s.units)} {t("Stück")}{skipped && <> {t("· nicht gemeldet:")} {skipped}</>}</>}
        {s.status === "failed" && <>{t("Bestandsübertragung fehlgeschlagen")}{s.products > 0 && t(" nach {v} Artikeln", { v: fmtNumber(s.products) })}: {s.error}</>}
      </span>
    </div>
  );
}

/** Fehlende SKUs in Shopify automatisch ergänzen (sonst führt Shopify die Variante nicht an der Logiful-Location) */
function FillSkuSection({ channelId, fill, missing }: { channelId: string; fill: boolean; missing: number }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (enabled: boolean) => api.channels.setFillMissingSku(tenantId, channelId, enabled),
    onSuccess: (_r, enabled) => {
      toast.success("Gespeichert", { description: enabled ? "Fehlende SKUs werden in den nächsten Minuten in Shopify ergänzt." : undefined });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  const on = save.isPending ? !!save.variables : fill;
  return (
    <section>
      <h3 className="font-semibold text-[14px]">{t("SKUs in Shopify")}</h3>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Shopify führt Varianten ohne SKU nicht an der Logiful-Location – Bestellungen damit gehen an einen anderen Standort (Split).")}{missing > 0 && <> {t("Derzeit")} <b className="text-fg">{fmtNumber(missing)}</b> {t("betroffen.")}</>}</p>
      <div className="mt-3">
        <Switch on={on} onChange={(v) => save.mutate(v)} label={<><span className="font-medium">{t("Fehlende SKU automatisch in Shopify ergänzen")}</span> <span className="text-fg-subtle">{t("· SKU aus dem Portal, sonst die Varianten-ID")}</span></>} />
      </div>
    </section>
  );
}

/** Nächtlicher Artikel-Abgleich – fängt verlorene Webhooks ab, gesperrte Felder bleiben unberührt */
function NightlySyncSection({ channelId, on, at }: { channelId: string; on: boolean; at: string | null }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (enabled: boolean) => api.channels.setNightlySync(tenantId, channelId, enabled),
    onSuccess: (_r, enabled) => {
      toast.success("Gespeichert", { description: enabled ? "Ab heute Nacht (3 Uhr) werden alle in Shopify geänderten Artikel abgeglichen." : undefined });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  const value = save.isPending ? !!save.variables : on;
  return (
    <section className="px-6 pt-6">
      <h3 className="font-semibold text-[14px]">{t("Nächtlicher Abgleich")}</h3>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Änderungen aus Shopify kommen sofort per Webhook. Zusätzlich gleicht Connect nachts alle seit dem letzten Lauf geänderten Artikel ab – falls einmal eine Meldung verloren ging. Im Portal gepflegte Felder (Schloss) werden nie überschrieben.")}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Switch on={value} onChange={(v) => save.mutate(v)} label={<span className="font-medium">{t("Jede Nacht um 3 Uhr abgleichen")}</span>} />
        {on && at && <span className="text-[12px] text-fg-subtle">{t("zuletzt")} {fmtRelative(at)}</span>}
      </div>
    </section>
  );
}

function ImportSettings({ channelId, initial, busy, onStarted }: { channelId: string; initial: ImportFilter; busy: boolean; onStarted: () => void }) {
  const { tenantId } = useTenant();
  const [f, setF] = useState<ImportFilter>(initial);
  const set = <K extends keyof ImportFilter>(k: K, v: ImportFilter[K]) => setF((x) => ({ ...x, [k]: v }));
  const facets = useQuery({ queryKey: ["catalog-facets", tenantId, channelId], queryFn: () => api.channels.facets(tenantId, channelId), staleTime: 10 * 60_000, retry: false });

  // Vorschau entprellt: Shopify zählt die passenden Varianten
  const [debounced, setDebounced] = useState(f);
  useEffect(() => {
    const tv = setTimeout(() => setDebounced(f), 350);
    return () => clearTimeout(tv);
  }, [f]);
  const preview = useQuery({ queryKey: ["catalog-preview", tenantId, channelId, debounced], queryFn: () => api.channels.preview(tenantId, channelId, debounced), placeholderData: (prev) => prev, retry: false });
  const stale = JSON.stringify(debounced) !== JSON.stringify(f) || preview.isFetching;
  const p = preview.data;
  const share = p && p.total ? Math.min(1, p.matching / p.total) : 0;
  const noStatus = f.mode === "filtered" && f.statuses.length === 0;

  const start = useMutation({
    mutationFn: () => api.channels.startImport(tenantId, channelId, f),
    onSuccess: () => { toast.success("Import gestartet", { description: msg("Shopify bereitet den Export vor – der Fortschritt erscheint oben.") }); onStarted(); },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Import konnte nicht gestartet werden") : "Import konnte nicht gestartet werden"),
  });

  const opts = useMemo(() => facets.data ?? { vendors: [], productTypes: [], tags: [] }, [facets.data]);

  return (
    <div className="flex flex-col min-h-full">
      <div className="px-6 pt-4 pb-3 border-b border-border flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-[14px]">{t("Artikelauswahl")}</h3>
          <p className="text-[12.5px] text-fg-muted">{t("Welche Shopify-Varianten ins Portal und damit ins Lager übertragen werden. Gilt auch für neue und geänderte Artikel.")}</p>
        </div>
        <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
          {([["filtered", msg("Nur passende Artikel")], ["all", msg("Alle Artikel")]] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => set("mode", k)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", f.mode === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{l}</button>
          ))}
        </div>
      </div>

      {f.mode === "filtered" ? (
        <div className="px-6">
          <Row label={t("Produktstatus")}>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => {
                const on = f.statuses.includes(s.key);
                return <button key={s.key} type="button" onClick={() => set("statuses", on ? f.statuses.filter((x) => x !== s.key) : [...f.statuses, s.key])} className={cx("h-7 px-3 rounded-lg border text-[12.5px] font-medium", on ? "border-ink bg-ink text-ink-fg" : "border-border hover:border-border-strong")}>{t(s.label)}</button>;
              })}
            </div>
            {noStatus && <p className="mt-1 text-[12px] text-danger">{t("Mindestens einen Status wählen.")}</p>}
          </Row>
          <Row label={t("Hersteller")} hint={t("leer = alle")}><TokenSelect value={f.vendors} onChange={(v) => set("vendors", v)} options={opts.vendors} placeholder={t("Hersteller wählen …")} /></Row>
          <Row label={t("Produkttyp")} hint={t("leer = alle")}><TokenSelect value={f.productTypes} onChange={(v) => set("productTypes", v)} options={opts.productTypes} placeholder={t("z. B. Gesichtspflege")} /></Row>
          <Row label={t("Mit Tag")} hint={t("mindestens einer")}><TokenSelect value={f.tagsAny} onChange={(v) => set("tagsAny", v)} options={opts.tags} placeholder={t("z. B. lager")} /></Row>
          <Row label={t("Ohne Tag")} hint={t("z. B. Dropshipping ausschließen")}><TokenSelect value={f.tagsExclude} onChange={(v) => set("tagsExclude", v)} options={opts.tags} placeholder={t("z. B. dropship")} /></Row>
          <Row label={t("SKU beginnt mit")} hint={t("leer = alle")}><TokenSelect value={f.skuPrefixes} onChange={(v) => set("skuPrefixes", v.filter((x) => /^[A-Za-z0-9._-]+$/.test(x)))} placeholder={t("z. B. NL-")} mono /></Row>
          <Row label={t("Außerdem")}>
            <div className="space-y-2.5">
              <Switch on={f.onlyShippable} onChange={(v) => set("onlyShippable", v)} label={<><span className="font-medium">{t("Nur versandpflichtige Varianten")}</span> <span className="text-fg-subtle">{t("· keine Gutscheine oder Dienstleistungen")}</span></>} />
              <Switch on={f.onlyTracked} onChange={(v) => set("onlyTracked", v)} label={<><span className="font-medium">{t("Nur mit Bestandsführung in Shopify")}</span></>} />
            </div>
          </Row>
          {facets.isError && <p className="pb-3 text-[12px] text-fg-subtle">{t("Vorschläge aus dem Shop gerade nicht verfügbar – Werte können trotzdem eingegeben werden.")}</p>}
        </div>
      ) : (
        <div className="px-6 py-4 text-[13px] text-fg-muted">{t("Alle Varianten werden übernommen (ohne SKU nur mit der Option unten) – auch Entwürfe und archivierte Produkte. Bei sehr großen Sortimenten dauert der Import einige Minuten, läuft aber stabil im Hintergrund weiter.")}</div>
      )}

      <div className="px-6 py-3 border-t border-border">
        <Switch
          on={f.skuFallback === "variantId"}
          onChange={(v) => set("skuFallback", v ? "variantId" : "skip")}
          label={<><span className="font-medium">{t("Varianten ohne SKU mit der Shopify-Varianten-ID als SKU übernehmen")}</span> <span className="text-fg-subtle">{t("· sonst werden sie übersprungen. Wird später eine SKU in Shopify nachgetragen, bleibt der Artikel im Portal und Lager unter der Varianten-ID.")}</span></>}
        />
      </div>

      <div className="sticky bottom-0 mt-auto px-6 py-4 border-t border-border bg-surface flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          {preview.isError ? (
            <p className="text-[13px] text-fg-muted">{t("Vorschau gerade nicht verfügbar.")}</p>
          ) : p ? (
            <>
              <p className={cx("text-[13px] transition-opacity", stale && "opacity-50")}>
                <b className="text-[18px] tabular-nums">{fmtNumber(p.matching)}</b> <span className="text-fg-muted">{t("von")} {fmtNumber(p.total)} {t("Varianten")}{!p.matchingExact && t(" (mindestens)")}</span>
              </p>
              <div className="mt-1.5 h-1.5 rounded-full bg-border overflow-hidden max-w-md"><div className={cx("h-full rounded-full bg-ink transition-all", stale && "opacity-50")} style={{ width: `${Math.max(share * 100, p.matching ? 1 : 0)}%` }} /></div>
              {p.postFilter && <p className="mt-1 text-[11.5px] text-fg-subtle">{t("Versandpflicht/Bestandsführung werden beim Import zusätzlich geprüft – es können etwas weniger werden.")}</p>}
            </>
          ) : (
            <Skeleton className="h-8 w-56" />
          )}
        </div>
        <Button variant="primary" className="h-10 px-5" disabled={busy || noStatus || (p?.matching === 0 && !stale)} loading={start.isPending} onClick={() => start.mutate()}>
          {busy ? t("Import läuft …") : p && !stale ? t("{v} Varianten importieren", { v: fmtNumber(p.matching) }) : t("Import starten")}
        </Button>
      </div>
    </div>
  );
}

const SKIP_LABELS: Record<string, string> = {
  no_sku: "ohne SKU",
  error: "fehlerhaft",
  status: "Produktstatus",
  vendor: "anderer Hersteller",
  product_type: "andere Produktart",
  tags: "ohne passenden Tag",
  tag_excluded: "ausgeschlossener Tag",
  sku_prefix: "anderer SKU-Präfix",
  not_tracked: "ohne Bestandsführung",
  not_shippable: "nicht versandpflichtig",
};

/** Link zur Variante im Shopify-Admin, damit der Kunde die SKU direkt nachtragen kann */
const shopifyAdminUrl = (shop: string, productGid: string, variantGid: string) =>
  `https://${shop}/admin/products/${productGid.split("/").pop()}/variants/${variantGid.split("/").pop()}`;

/** Nicht übernommene Varianten: Datenfehler (fehlende SKU) prominent mit Liste, Filtergründe nur als Zahlen */
function ImportProblems({ state, shop }: { state: ShopifyImportState; shop: string }) {
  const [open, setOpen] = useState(false);
  const reasons = Object.entries(state.skipReasons ?? {}).filter(([, n]) => n > 0);
  const noSku = state.skipReasons?.no_sku ?? 0;
  const errors = state.skipReasons?.error ?? 0;
  const filtered = reasons.filter(([r]) => r !== "no_sku" && r !== "error");
  const problems = state.problems ?? [];
  if (!reasons.length) return null;
  return (
    <div className="mt-3 space-y-2">
      {(noSku > 0 || errors > 0) && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft/60 p-3">
          <p className="font-medium text-fg">
            {noSku > 0 && <>{fmtNumber(noSku)} {t("Variante")}{noSku === 1 ? "" : "n"} {t("ohne SKU nicht übernommen")}</>}
            {noSku > 0 && errors > 0 && " · "}
            {errors > 0 && <>{fmtNumber(errors)} {t("fehlerhaft")}</>}
          </p>
          {noSku > 0 && (
            <p className="text-fg-muted mt-0.5">
              {t("Ohne SKU kann das Lager den Artikel nicht führen. Bitte in Shopify eine SKU hinterlegen und den Import erneut starten – oder oben „Varianten-ID als SKU übernehmen“ aktivieren.")}
            </p>
          )}
          {problems.length > 0 && (
            <>
              <button onClick={() => setOpen((o) => !o)} className="mt-1.5 text-[12.5px] font-medium underline underline-offset-2">
                {open ? t("Liste ausblenden") : t("Betroffene Varianten anzeigen{v}", { v: problems.length < noSku + errors ? t(" (erste {length})", { length: problems.length }) : "" })}
              </button>
              {open && (
                <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-border bg-surface divide-y divide-border">
                  {problems.map((p) => (
                    <li key={p.variantId} className="flex items-center gap-2 px-3 py-1.5">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{t(p.title)}</span>
                        {p.message && <span className="block truncate text-[11.5px] text-danger">{serverText(p.message)}</span>}
                      </span>
                      <Badge tone={p.reason === "no_sku" ? "warning" : "danger"}>{SKIP_LABELS[p.reason]}</Badge>
                      <a href={shopifyAdminUrl(shop, p.productId, p.variantId)} target="_blank" rel="noreferrer" className="text-[12px] font-medium text-fg-muted hover:text-fg hover:underline shrink-0">{t("In Shopify öffnen ↗")}</a>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
      {filtered.length > 0 && (
        <p className="text-[12px] text-fg-muted">
          {t("Durch den Filter ausgelassen:")} {filtered.map(([r, n]) => `${fmtNumber(n)} ${SKIP_LABELS[r] ?? r}`).join(" · ")}
        </p>
      )}
    </div>
  );
}

function ImportStatus({ state, shop }: { state: ShopifyImportState | null; shop: string }) {
  if (!state || state.status === "awaiting_filter") return null;
  const pct = state.status === "importing" && state.exported ? Math.round((state.processed / state.exported) * 100) : null;
  const tone = state.status === "failed" ? "border-danger/25 bg-danger-soft" : state.status === "done" ? "border-success/25 bg-success-soft/60" : "border-border bg-surface";
  return (
    <div className={cx("rounded-xl border px-4 py-3 text-[13px]", tone)}>
      <div className="flex items-start gap-3">
        {running(state) ? <Loader2 className="size-4 mt-0.5 animate-spin text-fg-muted" /> : state.status === "done" ? <CheckCircle2 className="size-4 mt-0.5 text-success" /> : <CircleAlert className="size-4 mt-0.5 text-danger" />}
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {state.status === "exporting" && t("Shopify bereitet den Export vor … {v}", { v: state.exported ? t("{v} Varianten", { v: fmtNumber(state.exported) }) : "" })}
            {state.status === "importing" && t("Artikel werden übernommen … {v} von {v2}", { v: fmtNumber(state.processed), v2: fmtNumber(state.exported) })}
            {state.status === "done" && t("Import abgeschlossen {v}", { v: state.finishedAt ? fmtRelative(state.finishedAt) : "" })}
            {state.status === "failed" && t("Import fehlgeschlagen")}
          </p>
          {(state.status === "importing" || state.status === "done") && (
            <p className="text-fg-muted mt-0.5 tabular-nums"><b className="text-fg">{fmtNumber(state.created)}</b> {t("neu ·")} {fmtNumber(state.updated)} {t("aktualisiert ·")} {fmtNumber(state.skipped)} {t("nicht übernommen")}</p>
          )}
          {state.status === "done" && <ImportProblems state={state} shop={shop} />}
          {state.status === "failed" && state.error && <p className="text-danger mt-0.5">{state.error}</p>}
          {pct != null && <div className="mt-2 h-1.5 rounded-full bg-border overflow-hidden"><div className="h-full bg-ink rounded-full transition-all" style={{ width: `${pct}%` }} /></div>}
        </div>
        <Store className="size-4 text-fg-subtle" />
      </div>
    </div>
  );
}

/** Fulfillment in Shopify automatisch anfordern – sonst muss der Händler je Auftrag „Fulfillment anfordern“ klicken */
/** Tracking-Link, der mit der Versandmeldung an Shopify geht */
function TrackingLinkSection({ channelId, mode }: { channelId: string; mode: "warehouse" | "dhl_de" }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (m: "warehouse" | "dhl_de") => api.channels.setTrackingLink(tenantId, channelId, m),
    onSuccess: () => {
      toast.success(t("Gespeichert"), { description: t("Gilt für alle Sendungen, die ab jetzt an Shopify gemeldet werden.") });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error(t("Speichern fehlgeschlagen")),
  });
  const current = save.isPending ? save.variables : mode;
  const options = [
    { value: "warehouse" as const, title: msg("Wie vom Lager gemeldet"), text: msg("Dienstleister und Link kommen aus der Versandmeldung. Fehlt ein Link, ergänzt Shopify ihn anhand des Dienstleisters.") },
    { value: "dhl_de" as const, title: msg("Immer DHL-Sendungsverfolgung (dhl.de)"), text: msg("DHL- und Warenpost-Sendungen gehen immer mit dem Link zu dhl.de an Shopify – unabhängig davon, wie Shopify den Dienstleister zuordnet. Andere Dienstleister bleiben unverändert.") },
  ];
  return (
    <section>
      <h3 className="font-semibold text-[14px]">{t("Tracking-Link in Shopify")}</h3>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Diesen Link sehen eure Kunden in der Versandbestätigung und in ihrem Kundenkonto.")}</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {options.map((o) => (
          <button key={o.value} type="button" disabled={save.isPending} onClick={() => o.value !== current && save.mutate(o.value)} aria-pressed={current === o.value} className={cx("rounded-lg border p-3 text-left disabled:cursor-default", current === o.value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}>
            <span className="block text-[13px] font-medium">{t(o.title)}</span>
            <span className="block text-[12px] text-fg-muted mt-0.5">{t(o.text)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function AutoRequestSection({ channelId, mode, editable }: { channelId: string; mode: "all" | "paid" | "off"; editable: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (m: "all" | "paid" | "off") => api.channels.setAutoRequest(tenantId, channelId, m),
    onSuccess: () => {
      toast.success("Gespeichert", { description: msg("Offene Aufträge werden in den nächsten Minuten abgerufen.") });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  const current = save.isPending ? save.variables : mode;
  const options = [
    { value: "all" as const, title: msg("Alle Aufträge automatisch anfordern"), text: msg("Neue Aufträge an der Logiful-Location kommen ohne Klick ins Portal. Zahlungs- und andere Sperren prüft das Portal.") },
    { value: "paid" as const, title: msg("Nur bezahlte automatisch anfordern"), text: msg("Unbezahlte Aufträge (z. B. Vorkasse) bleiben in Shopify, bis die Zahlung eingeht.") },
    { value: "off" as const, title: msg("Nicht automatisch"), text: msg("Der Händler klickt in Shopify je Auftrag auf „Fulfillment anfordern“.") },
  ];
  return (
    <section>
      <h3 className="font-semibold text-[14px]">{t("Wie kommen Aufträge zu Logiful?")}</h3>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Shopify übergibt Aufträge an Logiful erst nach „Fulfillment anfordern“. Das Portal kann das automatisch übernehmen – geprüft wird alle 3 Minuten.")}</p>
      <div className="mt-3 grid gap-2">
        {options.map((o) => (
          <label key={o.value} className={cx("flex items-start gap-2.5 rounded-xl border p-3 transition-colors", current === o.value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60", editable ? "cursor-pointer" : "cursor-default")}>
            <input type="radio" className="mt-1 accent-ink" checked={current === o.value} disabled={!editable} onChange={() => save.mutate(o.value)} />
            <span>
              <span className="block text-[13px] font-medium">{t(o.title)}</span>
              <span className="block text-[12px] text-fg-muted">{t(o.text)}</span>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

/** An welchen Shopify-Artikeln der Logiful-Standort geführt wird (Bestellweiterleitung und Bestandsmeldung) */
/** Bestandsmeldung an Shopify ein/aus – aus, wenn der Kunde den Bestand in Shopify selbst pflegt */
function PushStockSection({ channelId, on }: { channelId: string; on: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (enabled: boolean) => api.channels.setPushStock(tenantId, channelId, enabled),
    onSuccess: (r) => {
      toast.success(r.pushStock ? t("Bestand wird wieder gemeldet") : t("Bestandsmeldung ausgeschaltet"), { description: r.pushStock ? t("Alle Bestände werden einmal vollständig an Shopify übertragen.") : t("Connect ändert keine Bestände in Shopify mehr.") });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error(t("Speichern fehlgeschlagen")),
  });
  const current = save.isPending ? save.variables : on;
  const options = [
    { value: true, title: t("Logiful meldet den Bestand"), text: t("Verkaufbarer Lagerbestand am Logiful-Standort – Änderungen in Sekunden. Empfohlen: kein Überverkauf.") },
    { value: false, title: t("Wir pflegen den Bestand in Shopify"), text: t("Connect ändert keine Bestände in Shopify – weder laufend noch beim Aktivieren neuer Artikel.") },
  ];
  return (
    <section>
      <h3 className="font-semibold text-[14px]">{t("Bestand an Shopify melden")}</h3>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Aufträge kommen in beiden Fällen bei Logiful an. Pflegt ihr den Bestand selbst, kann Shopify mehr verkaufen, als im Lager liegt.")}</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {options.map((o) => (
          <button key={String(o.value)} type="button" disabled={save.isPending} onClick={() => o.value !== current && save.mutate(o.value)} aria-pressed={current === o.value} className={cx("rounded-lg border p-3 text-left disabled:cursor-default", current === o.value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}>
            <span className="block text-[13px] font-medium">{t(o.title)}</span>
            <span className="block text-[12px] text-fg-muted mt-0.5">{t(o.text)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function LocationScopeSection({ channelId, scope, editable }: { channelId: string; scope: "all" | "released"; editable: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (s: "all" | "released") => api.channels.setLocationScope(tenantId, channelId, s),
    onSuccess: () => {
      toast.success("Gespeichert", { description: msg("Neue Artikel werden in den nächsten Minuten am Logiful-Standort geführt.") });
      void qc.invalidateQueries({ queryKey: ["channel", tenantId, channelId] });
    },
    onError: () => toast.error("Speichern fehlgeschlagen"),
  });
  const current = save.isPending ? save.variables : scope;
  const options = [
    { value: "all" as const, title: msg("Alle importierten Artikel"), text: msg("Jeder ins Portal übernommene Artikel wird in Shopify am Logiful-Standort geführt.") },
    { value: "released" as const, title: msg("Nur für Logiful freigegebene"), text: msg("Nur Artikel, die im FFN für Logiful freigegeben sind. Andere bleiben bei euren eigenen Standorten.") },
  ];
  return (
    <section>
      <h3 className="font-semibold text-[14px]">{t("Logiful-Standort in Shopify")}</h3>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Shopify leitet Bestellungen nur für Artikel an Logiful weiter, die am Logiful-Standort geführt werden – dort meldet das Portal auch den Bestand.")}</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={!editable || save.isPending}
            onClick={() => o.value !== current && save.mutate(o.value)}
            aria-pressed={current === o.value}
            className={cx("rounded-lg border p-3 text-left disabled:cursor-default", current === o.value ? "border-ink bg-surface-2" : "border-border hover:bg-surface-2/60")}
          >
            <span className="block text-[13px] font-medium">{t(o.title)}</span>
            <span className="block text-[12px] text-fg-muted mt-0.5">{t(o.text)}</span>
          </button>
        ))}
      </div>
      <p className="mt-2 text-[11.5px] text-fg-subtle">{t("Bereits zugeordnete Artikel bleiben zugeordnet – das Entfernen eines Standorts löscht in Shopify dort den Bestand und bleibt deshalb Handarbeit.")}</p>
    </section>
  );
}
